/**
 * QuestionnaireStepper — 2-step progress indicator shown above the form card.
 *
 * v2 onboarding (May 2026): identity (PAN/DOB) was moved out of onboarding
 * and is now collected at service creation when missing. So the stepper
 * shows just "About you" + "Your finances".
 *
 * Shows numbered circles (done → tick, current → amber, upcoming → outline)
 * connected by a single fill-bar whose width tracks step progress.
 */
import { CheckCircle2 } from 'lucide-react';
import { PALETTE_FOREST, PALETTE_AMBER } from '../tokens';

const STEP_LABELS = ['About you', 'Your finances'];
const TOTAL_STEPS = STEP_LABELS.length;

interface QuestionnaireSteperProps {
  currentStep: number; // 1-based
}

export function QuestionnaireStepper({ currentStep }: QuestionnaireSteperProps) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        {STEP_LABELS.map((label, i) => {
          const n       = i + 1;
          const done    = currentStep > n;
          const current = currentStep === n;
          return (
            <div key={n} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all"
                style={{
                  backgroundColor: done ? PALETTE_FOREST : current ? PALETTE_AMBER : 'transparent',
                  color:           done || current ? '#fff' : PALETTE_FOREST,
                  border:          done || current ? 'none' : `2px solid ${PALETTE_FOREST}`,
                  opacity:         done || current ? 1 : 0.4,
                }}
                aria-label={`Step ${n}: ${label}${done ? ' (completed)' : current ? ' (current)' : ''}`}
              >
                {done ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : n}
              </div>
              <span
                className="text-xs font-medium hidden sm:block"
                style={{ color: current ? PALETTE_FOREST : 'hsl(var(--muted-foreground))' }}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Connector fill-bar */}
      <div className="relative h-1 rounded-full" style={{ backgroundColor: 'hsl(var(--border))' }}>
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-300"
          style={{
            width:           `${((currentStep - 1) / (TOTAL_STEPS - 1)) * 100}%`,
            backgroundColor: PALETTE_FOREST,
          }}
        />
      </div>
    </div>
  );
}
